import { Check } from 'lucide-react'
import { Link } from 'react-router'
import { tint } from '@/lib/paper'
import { photoUrl } from '@/lib/photos'
import { cn } from '@/lib/utils'

// A recipe in a grid: the photo of its card first, its name and one quiet line under it. A recipe kept only as
// typed words shows its name on a plain tint instead of a picture. With onToggle it's picked instead of opened, as
// photos are: a picked recipe draws in and shows a check.
export function RecipeTile({
  recipe,
  index,
  line,
  picked,
  onToggle,
}: {
  recipe: { id: string; title: string; cover: string | null }
  index: number
  line: string
  picked?: boolean
  onToggle?: () => void
}) {
  const className =
    'group relative flex w-full flex-col gap-3 rounded-[1.5rem] text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50'
  const body = (
    <>
      <span
        aria-hidden="true"
        className={cn(
          'block aspect-[4/5] overflow-hidden rounded-[1.5rem] shadow-paper transition-[translate,box-shadow,scale] duration-300 ease-settle',
          onToggle ? picked && 'scale-[0.92]' : 'group-hover:translate-y-[-3px] group-hover:shadow-lift',
          tint(index),
        )}
      >
        {recipe.cover ? (
          <img
            src={photoUrl(recipe.cover, 'thumb')}
            alt=""
            loading="lazy"
            decoding="async"
            className="size-full object-cover transition-[scale] duration-300 ease-settle group-hover:scale-[1.03]"
          />
        ) : (
          <span className="grid size-full place-items-center p-4 text-center text-lg font-semibold tracking-tight text-balance">
            {recipe.title}
          </span>
        )}
      </span>
      <span className="flex flex-col gap-0.5 px-1">
        <span className="truncate font-semibold">{recipe.title}</span>
        <span className="truncate text-sub text-muted-foreground">{line}</span>
      </span>
    </>
  )
  if (!onToggle) {
    return (
      <Link to={`/recipe/${recipe.id}`} className={className}>
        {body}
      </Link>
    )
  }
  return (
    <button type="button" aria-pressed={!!picked} onClick={onToggle} className={className}>
      {body}
      <span
        aria-hidden="true"
        className={cn(
          'absolute top-3 right-3 grid size-6 place-items-center rounded-full border-2 transition-colors',
          picked
            ? 'border-brand bg-brand text-brand-foreground'
            : 'border-viewer-foreground bg-viewer/25 text-transparent',
        )}
      >
        <Check className="size-3.5" strokeWidth={3} />
      </span>
    </button>
  )
}
